/**
 * Именованные состояния страницы или блока: загрузка, пусто, ничего не найдено по поиску
 * (filter-empty макета под заголовками таблицы), ошибка, нет прав, временная недоступность
 * сервиса (сессия при этом сохраняется).
 * Один вид на весь интерфейс (empty-state макета); «нет данных» и «нет прав» —
 * разные состояния, ошибка предлагает повторить.
 *
 * Правило для списков и карточек: запрос в ошибке (isError) показывается как
 * state="error" с retry=refetch, а не пустой таблицей и не «Данных пока нет»:
 * сбой API и пустой ответ пользователь должен отличать. Ошибки действий (мутаций)
 * показывает общее уведомление (src/lib/queryClient.ts), если экран не показывает их сам.
 */

import { useTranslation } from 'react-i18next';
import { WorkspaceIcon, type WorkspaceIconName } from './WorkspaceIcon';

export type WorkspaceStateKind =
	| 'loading'
	| 'empty'
	| 'filtered'
	| 'error'
	| 'forbidden'
	| 'unavailable';

interface WorkspaceStateProps {
	state: WorkspaceStateKind;
	title?: string;
	hint?: string;
	retry?: () => void;
}

const ICON: Record<Exclude<WorkspaceStateKind, 'loading'>, WorkspaceIconName> = {
	empty: 'search',
	filtered: 'search',
	error: 'alert',
	forbidden: 'lock',
	unavailable: 'alert',
};

export function WorkspaceState({ state, retry, title, hint }: WorkspaceStateProps) {
	const { t } = useTranslation('common');

	if (state === 'loading') {
		return (
			<div data-workspace-state={state} className="ws-empty-state" role="status" aria-live="polite">
				<p>{t('workspaceState.loading')}</p>
			</div>
		);
	}

	return (
		<div
			data-workspace-state={state}
			className={state === 'filtered' ? 'ws-empty-state ws-filter-empty' : 'ws-empty-state'}
		>
			<WorkspaceIcon name={ICON[state]} size={28} />
			<h3>{title ?? t(`workspaceState.${state}.title`)}</h3>
			<p>{hint ?? t(`workspaceState.${state}.hint`)}</p>
			{(state === 'error' || state === 'unavailable') && retry && (
				<button type="button" className="ws-btn" onClick={retry}>
					{t('workspaceState.retry')}
				</button>
			)}
		</div>
	);
}
